import { RoundedButton } from '@/components/rounded-button';
import { SeasonStatsCard } from '@/components/season-stats-card';
import { InputGroup } from '@/components/ui/input-group';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentUserData } from '@/hooks/useCurrentUserData';
import { useSeasonOneStats } from '@/hooks/useSeasonOneStats';
import { Card, Heading, Image, Input, Show, Spinner, Stack, VStack } from '@chakra-ui/react';
import { useLocalStorage } from 'usehooks-ts';
import airplaneSvg from '/assets/onboarding/airplane.svg';
import convertMilesSvg from '/assets/onboarding/convert-miles.svg';
import xpCircleSvg from '/assets/pp-circle.svg';

export const ConvertMiles = ({ goToNextStep }: { goToNextStep: () => void }) => {
  const { address, isLoading } = useAuth();
  const { miles, isLoading: isLoadingSeasonOneStats, bonusPoints } = useSeasonOneStats();

  const { currentUserData } = useCurrentUserData();

  const [hasConvertedMiles, setHasConvertedMiles] = useLocalStorage(`${address}:hasConvertedMiles`, false);

  const handleClick = () => {
    if (!hasConvertedMiles) {
      setHasConvertedMiles(true);
      return;
    }
    goToNextStep();
  };
  if (!miles || !currentUserData) return null;

  return (
    <Show when={!isLoading || !isLoadingSeasonOneStats} fallback={<Spinner />}>
      <Stack gap={6} alignItems="start" w="full" flexDirection={['column', 'column', 'row']}>
        <SeasonStatsCard showProfile order={[2, 2, 1]} w={['full', 'full', 'auto']} />
        <Card.Root p={[0, 0, 4]} flex={1.2} rounded="24px" order={[1, 1, 2]}>
          <Card.Header textAlign="center" gap={6}>
            <Image src={convertMilesSvg} boxSize={16} alignSelf="center" />
            <VStack gap={2} alignItems="flex-start" textAlign="center" justifyContent="center">
              <Heading
                fontSize={['32px', '32px', '40px']}
                fontWeight="700"
                letterSpacing="-0.8px"
                lineHeight="48px"
                mx="auto"
              >
                Convert Your Miles
              </Heading>
              <Heading size={['lg', 'lg', 'xl']} fontWeight="700" letterSpacing="-0.47px" lineHeight="28px" mx="auto">
                Thank you for participating in Plume Testnet
              </Heading>
              <Heading
                size={['lg', 'lg', 'xl']}
                fontWeight="500"
                letterSpacing="-0.47px"
                lineHeight="28px"
                color="text.secondary"
              >
                Get a heard start by converting your Plume Miles into PP!
              </Heading>
            </VStack>
          </Card.Header>
          <Card.Body gap={4}>
            {hasConvertedMiles ? (
              <VStack gap={4}>
                <InputGroup
                  startElement={<Image src={xpCircleSvg} />}
                  endElement={
                    <Heading
                      fontSize="lg"
                      fontWeight="500"
                      lineHeight="24px"
                      letterSpacing="-0.33px"
                      color="#577668"
                      mr={3}
                    >
                      Plume Points
                    </Heading>
                  }
                  w="full"
                >
                  <Input
                    value={bonusPoints}
                    colorPalette="green"
                    readOnly
                    bgColor="#EBF6F1"
                    fontWeight="bold"
                    size="2xl"
                    h={84}
                    borderColor="transparent"
                    rounded="16px"
                  />
                </InputGroup>
              </VStack>
            ) : (
              <InputGroup
                startElement={<Image src={airplaneSvg} />}
                endElement={
                  <Heading
                    fontSize="lg"
                    fontWeight="500"
                    lineHeight="24px"
                    letterSpacing="-0.33px"
                    color="text.secondary"
                  >
                    Plume Points
                  </Heading>
                }
              >
                <Input
                  value={miles}
                  readOnly
                  bgColor="bg.muted"
                  fontWeight="bold"
                  size="2xl"
                  h={84}
                  borderColor="bg.muted"
                  rounded="16px"
                />
              </InputGroup>
            )}
            <RoundedButton size="2xl" onClick={handleClick}>
              {hasConvertedMiles ? 'Continue' : 'Convert Miles'}
            </RoundedButton>
          </Card.Body>
        </Card.Root>
      </Stack>
    </Show>
  );
};
